import { useEffect, useId, useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { SelectPopover } from '../../../components/SelectPopover';
import { TextInput } from '../../../components/TextInput';
import type {
  BillingCollectionMethod,
  BillingInterval,
  BillingPlan,
  CreateStripeSubscriptionRequest,
} from '../../../types/adminBilling';
import {
  COLLECTION_METHOD_LABEL,
  INTERVAL_LABEL,
  centsToDollarsInput,
  collectionMethodForLabel,
  dollarsToCents,
  formatMoney,
} from './billingFormat';

interface StripeSubscriptionModalProps {
  isOpen: boolean;
  organizationName: string;
  /** Where every default comes from. Null only while the summary is still loading. */
  plan: BillingPlan | null;
  /**
   * Whether Stripe can take money with what is on file **right now**.
   *
   * The single field this may be driven from. A stored id and a label are not the same as a usable
   * method: an ACH mandate waiting on two microdeposit amounts has both and cannot be charged.
   */
  canChargeAutomatically: boolean;
  /** What is on file, for the sentence explaining why auto-charge is or is not offered. */
  paymentMethodLabel: string | null;
  saving: boolean;
  error: string | null;
  onCreate: (request: CreateStripeSubscriptionRequest) => void;
  onClose: () => void;
}

const INTERVALS: readonly BillingInterval[] = ['Monthly', 'Annual'];
const INTERVAL_LABELS = INTERVALS.map((interval) => INTERVAL_LABEL[interval]);

const intervalForLabel = (label: string): BillingInterval | null =>
  INTERVALS.find((interval) => INTERVAL_LABEL[interval] === label) ?? null;

const filled = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * The subscription that makes Stripe actually raise this organization's invoices.
 *
 * **Every field is optional and the plan supplies what is left out**, which is the ordinary case:
 * the deal was recorded when the plan was saved, and retyping it here is how the two come to
 * disagree. So the fields carry the plan's figures as placeholders rather than as values - a
 * prefilled amount is an amount that gets sent, and a sent amount overrides the plan's for good.
 *
 * **Collection defaults to Stripe emailing the invoice**, which is what a college accounts payable
 * department expects and needs nothing on file. Charging automatically is only offered when a
 * verified method is on file, because the API refuses it with a 409 otherwise - Stripe would
 * otherwise be asked to create a subscription whose first invoice can never be paid, and expire it
 * a day later. When it is not on offer the reason is on screen, rather than a choice that fails.
 */
export const StripeSubscriptionModal = ({
  isOpen,
  organizationName,
  plan,
  canChargeAutomatically,
  paymentMethodLabel,
  saving,
  error,
  onCreate,
  onClose,
}: StripeSubscriptionModalProps) => {
  const fieldId = useId();
  const [collectionMethod, setCollectionMethod] =
    useState<BillingCollectionMethod>('SendInvoice');
  const [daysUntilDue, setDaysUntilDue] = useState('');
  const [startOn, setStartOn] = useState('');
  const [amount, setAmount] = useState('');
  const [interval, setInterval] = useState('');
  const [priceId, setPriceId] = useState('');
  const [description, setDescription] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    /*
      Reopened on the safe choice every time, never on whatever was picked last. The one that needs
      no payment method is the one a half-remembered form should land on.
    */
    setCollectionMethod('SendInvoice');
    setDaysUntilDue('');
    setStartOn('');
    setAmount('');
    setInterval('');
    setPriceId('');
    setDescription('');
    setAmountError(null);
    setTermsError(null);
  }, [isOpen]);

  /*
    The method can be removed at Stripe while this dialog is open. Falling back rather than sending
    a choice that is now refused: the worst case is an invoice Stripe emails, which is the default
    anyway.
  */
  const effectiveMethod: BillingCollectionMethod =
    canChargeAutomatically ? collectionMethod : 'SendInvoice';
  const sendsInvoice = effectiveMethod === 'SendInvoice';

  const methodChoices = canChargeAutomatically
    ? [COLLECTION_METHOD_LABEL.SendInvoice, COLLECTION_METHOD_LABEL.ChargeAutomatically]
    : [COLLECTION_METHOD_LABEL.SendInvoice];

  const handleConfirm = () => {
    const priceIdValue = filled(priceId);
    const amountValue = filled(amount);

    if (priceIdValue && amountValue) {
      setAmountError('Give either an existing Stripe price id or an amount, not both.');
      return;
    }

    let amountCents: number | undefined;
    if (amountValue) {
      const parsed = dollarsToCents(amountValue);
      if (parsed === null || parsed <= 0) {
        setAmountError('Enter an amount in dollars, for example 1234.56, or leave it blank.');
        return;
      }
      amountCents = parsed;
    }
    setAmountError(null);

    let days: number | undefined;
    const daysValue = filled(daysUntilDue);
    if (sendsInvoice && daysValue) {
      if (!/^\d+$/.test(daysValue) || Number(daysValue) > 365) {
        setTermsError('Payment terms must be a whole number of days between 0 and 365.');
        return;
      }
      days = Number(daysValue);
    }
    setTermsError(null);

    const intervalValue = intervalForLabel(interval);

    onCreate({
      collectionMethod: effectiveMethod,
      ...(priceIdValue ? { priceId: priceIdValue } : {}),
      ...(amountCents !== undefined ? { amountCents } : {}),
      ...(intervalValue ? { interval: intervalValue } : {}),
      /*
        Payment terms are refused outright on an auto-charged subscription, so they are dropped
        here rather than sent and bounced. There is nothing for Stripe to apply them to.
      */
      ...(days !== undefined ? { daysUntilDue: days } : {}),
      ...(filled(startOn) ? { startOn: filled(startOn) } : {}),
      ...(filled(description) ? { description: filled(description) } : {}),
    });
  };

  const planAmount = plan
    ? `${formatMoney(plan.amountCents, plan.currency)} ${INTERVAL_LABEL[plan.interval].toLowerCase()}`
    : 'the plan';

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Set up the recurring invoice"
      message={`Stripe raises ${organizationName}'s invoices on a schedule from this point. Until it exists, nothing invoices this organization at all.`}
      confirmText={saving ? 'Creating...' : 'Create subscription'}
      cancelText="Cancel"
      busy={saving}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="billing-form">
        <p className="billing-form__hint">
          Leave everything blank and it bills what the plan says: {planAmount}. Anything filled in
          here is what Stripe bills instead, and the plan stays as the record of what was agreed.
        </p>

        <div className="billing-form__field">
          <label className="billing-panel__label" htmlFor={`${fieldId}-collection`}>
            How Stripe collects it
          </label>
          <SelectPopover
            id={`${fieldId}-collection`}
            testId="billing-stripe-collection"
            options={methodChoices}
            value={COLLECTION_METHOD_LABEL[effectiveMethod]}
            onChange={(label) =>
              setCollectionMethod(collectionMethodForLabel(label) ?? 'SendInvoice')
            }
            placeholder={COLLECTION_METHOD_LABEL.SendInvoice}
            clearable={false}
            disabled={saving}
            fullWidth
          />
          <p className="billing-form__hint" data-testid="billing-stripe-collection-hint">
            {canChargeAutomatically
              ? `Stripe can charge ${paymentMethodLabel ?? 'the method on file'} when each invoice is due, or email the invoice and let the college pay it.`
              : 'Stripe can only email the invoice: charging automatically needs a verified payment method on file, and Stripe refuses a subscription that has nothing to charge. Set one up above to offer it.'}
          </p>
        </div>

        {sendsInvoice && (
          <TextInput
            label="Payment terms in days (optional)"
            inputMode="numeric"
            placeholder={plan ? String(plan.paymentTermsDays) : '30'}
            value={daysUntilDue}
            onChange={(e) => setDaysUntilDue(e.target.value)}
            error={termsError ?? undefined}
            disabled={saving}
            data-testid="billing-stripe-days-until-due"
          />
        )}

        <TextInput
          label="Start billing on (optional)"
          type="date"
          value={startOn}
          onChange={(e) => setStartOn(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-start-on"
        />
        <p className="billing-form__hint">
          Blank starts billing now. A future date means nothing is billed until then, and Stripe
          reports the subscription as trialing in the meantime - that is Stripe&apos;s word for
          &quot;has not started billing yet&quot;, not a free trial.
        </p>

        <TextInput
          label="Amount in dollars (optional)"
          inputMode="decimal"
          placeholder={plan ? centsToDollarsInput(plan.amountCents) : '1234.56'}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amountError ?? undefined}
          disabled={saving}
          data-testid="billing-stripe-amount"
        />

        <div className="billing-form__field">
          <label className="billing-panel__label" htmlFor={`${fieldId}-interval`}>
            How often it repeats (optional)
          </label>
          <SelectPopover
            id={`${fieldId}-interval`}
            testId="billing-stripe-interval"
            options={INTERVAL_LABELS}
            value={interval}
            onChange={setInterval}
            placeholder={plan ? `Use the plan's (${INTERVAL_LABEL[plan.interval]})` : "Use the plan's"}
            disabled={saving}
            fullWidth
          />
        </div>

        <TextInput
          label="Existing Stripe price id (optional)"
          placeholder="price_..."
          value={priceId}
          onChange={(e) => setPriceId(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-price-id"
        />
        <TextInput
          label="Invoice line description (optional)"
          placeholder={`${organizationName} subscription`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
          data-testid="billing-stripe-description"
        />
        <p className="billing-form__hint">
          A price id uses a price that already exists in the Stripe account, so the amount and the
          repeat above are Stripe&apos;s rather than ours. Give one or the other, not both.
        </p>

        {error && (
          <p
            className="billing-form__error"
            role="alert"
            data-testid="billing-stripe-subscription-error"
          >
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
